import type { GeometryElement as JXGElement } from 'jsxgraph';
import type { PortMap, PointPort, EdgePort } from '../../../port';
import { computeAngleCenter } from '../../../port';
import type { SolvedRhombus } from '../../../analytics';
import type { PlacedShape } from '../../../placement';
import type { GeometryPadElementContext, CreatedGeometryElement } from '../../element.model';
import { EDGE_NAMES } from '../rectangle/rectangle.model';
import { resolveGeometryStyle } from '../../coloring';
import {
  initShapeCreatorContext,
  HIDDEN_POINT_ATTRS,
  createBorderStyles,
  createGeneralAngleMarkers,
  applyEdgeVisibilityOverrides,
  QUAD_ANGLE_KEYS,
  QUAD_VERTEX_NAMES,
  resolveBaseVertexStyle,
  buildVertexPerIndex,
} from '../shape-creator.util';
import type { RhombusElement } from './rhombus.model';

/**
 * Create a rhombus element with angle-based dependent points.
 *
 * A and B are control points (base edge).
 * C and D are computed via the included angle offset with equal side length.
 */
export function createRhombus(
  element: RhombusElement & { id: string },
  placed: PlacedShape,
  solved: SolvedRhombus,
  context: GeometryPadElementContext,
): CreatedGeometryElement {
  const ctx = initShapeCreatorContext(element, context);
  const { elementFactory, elementDisplay, board, jsxObjects, labels, styleCtx } = ctx;
  const givenKeys = new Set(Object.keys(element.params ?? {}));

  const vA = placed.vertices['A'] as [number, number];
  const vB = placed.vertices['B'] as [number, number];
  const vD = placed.vertices['D'] as [number, number];

  const pA = board.create('point', vA, { ...HIDDEN_POINT_ATTRS, name: 'A' }) as JXGElement;
  const pB = board.create('point', vB, { ...HIDDEN_POINT_ATTRS, name: 'B' }) as JXGElement;
  const pD = board.create('point', vD, { ...HIDDEN_POINT_ATTRS, name: 'D' }) as JXGElement;

  const tA = pA as unknown as { X: () => number; Y: () => number };
  const tB = pB as unknown as { X: () => number; Y: () => number };
  const tD = pD as unknown as { X: () => number; Y: () => number };

  // C = B + (D - A) to maintain parallelogram property
  const pC = board.create('point', [() => tB.X() + (tD.X() - tA.X()), () => tB.Y() + (tD.Y() - tA.Y())], {
    ...HIDDEN_POINT_ATTRS,
    name: 'C',
  }) as JXGElement;

  // --- Build border styles using resolved cascade ---
  const RHOMBUS_PROP = { bottom: 's', right: 's', top: 's', left: 's' };
  const borderPerEdge = createBorderStyles(EDGE_NAMES, RHOMBUS_PROP, 'Rhombus', element, givenKeys, ctx);

  const polyStyle = resolveGeometryStyle('polygon', 'Rhombus', styleCtx, elementDisplay);
  const borderStyle = resolveGeometryStyle('line', 'Rhombus', styleCtx, elementDisplay);
  const polygon = elementFactory.polygon({
    vertices: [pA, pB, pC, pD],
    name: element.id,
    vertexNames: ['A', 'B', 'C', 'D'],
    style: {
      fillColor: polyStyle.fillColor,
      fillOpacity: polyStyle.fillOpacity,
      strokeWidth: 0,
      vertex: resolveBaseVertexStyle('Rhombus', ctx),
      vertexPerIndex: buildVertexPerIndex(QUAD_VERTEX_NAMES, 'Rhombus', ctx),
      border: { strokeWidth: borderStyle.strokeWidth ?? 2, color: borderStyle.color },
      borderPerEdge,
    },
  });

  jsxObjects.push(polygon.polygon, ...polygon.vertices, ...polygon.borders);
  jsxObjects.push(...polygon.helpers);
  labels.push(...polygon.borderLabels);

  // --- Create angle markers (always created) ---
  // Rhombus: opposite angles are equal (A=C, B=D), only label A and B.
  const angleElements = createGeneralAngleMarkers(polygon.vertices, 'Rhombus', element, givenKeys, ctx, 2);

  // --- Apply per-edge visibility overrides ---
  applyEdgeVisibilityOverrides(polygon.borders, EDGE_NAMES, elementDisplay);

  const tC = pC as unknown as { X: () => number; Y: () => number };
  const ports = buildRhombusPorts(tA, tB, tC, tD);

  // Named objects for PATCH_DISPLAY targeting
  const namedObjects: Record<string, JXGElement> = {};
  for (let i = 0; i < EDGE_NAMES.length; i++) {
    namedObjects[`edge.${EDGE_NAMES[i]}`] = polygon.borders[i];
  }
  for (let i = 0; i < QUAD_ANGLE_KEYS.length; i++) {
    namedObjects[`angle.${QUAD_ANGLE_KEYS[i]}`] = angleElements[i];
  }
  namedObjects['point.A'] = polygon.vertices[0];
  namedObjects['point.B'] = polygon.vertices[1];
  namedObjects['point.C'] = polygon.vertices[2];
  namedObjects['point.D'] = polygon.vertices[3];

  return {
    jsxObjects,
    labels,
    ports,
    namedObjects,
    controlPoints: [pA, pB],
    vertexPoints: { A: pA, B: pB, C: pC, D: pD },
    destroy: () => {
      for (const label of labels) {
        board.removeObject(label);
      }
      for (const obj of jsxObjects) {
        board.removeObject(obj);
      }
    },
  };
}

function buildRhombusPorts(
  a: { X: () => number; Y: () => number },
  b: { X: () => number; Y: () => number },
  c: { X: () => number; Y: () => number },
  d: { X: () => number; Y: () => number },
): PortMap {
  return {
    'point.A': () => [a.X(), a.Y()] as PointPort,
    'point.B': () => [b.X(), b.Y()] as PointPort,
    'point.C': () => [c.X(), c.Y()] as PointPort,
    'point.D': () => [d.X(), d.Y()] as PointPort,

    'edge.bottom': () => ({ start: [a.X(), a.Y()], end: [b.X(), b.Y()] }) as EdgePort,
    'edge.right': () => ({ start: [b.X(), b.Y()], end: [c.X(), c.Y()] }) as EdgePort,
    'edge.top': () => ({ start: [c.X(), c.Y()], end: [d.X(), d.Y()] }) as EdgePort,
    'edge.left': () => ({ start: [d.X(), d.Y()], end: [a.X(), a.Y()] }) as EdgePort,

    'angleCenter.A': () => computeAngleCenter(a, d, b),
    'angleCenter.B': () => computeAngleCenter(b, a, c),
    'angleCenter.C': () => computeAngleCenter(c, b, d),
    'angleCenter.D': () => computeAngleCenter(d, c, a),

    s: () => dist(a, b),
    h: () => {
      const bx = b.X() - a.X();
      const by = b.Y() - a.Y();
      const bLen = Math.sqrt(bx * bx + by * by);
      if (bLen === 0) {
        return 0;
      }
      const px = d.X() - a.X();
      const py = d.Y() - a.Y();
      return Math.abs(bx * py - by * px) / bLen;
    },
    angle: () => {
      const dx1 = b.X() - a.X();
      const dy1 = b.Y() - a.Y();
      const dx2 = d.X() - a.X();
      const dy2 = d.Y() - a.Y();
      const dot = dx1 * dx2 + dy1 * dy2;
      const cross = dx1 * dy2 - dy1 * dx2;
      return (Math.abs(Math.atan2(cross, dot)) * 180) / Math.PI;
    },
    d1: () => dist(a, c),
    d2: () => dist(b, d),
    area: () => {
      const bx = b.X() - a.X();
      const by = b.Y() - a.Y();
      const px = d.X() - a.X();
      const py = d.Y() - a.Y();
      return Math.abs(bx * py - by * px);
    },
    perimeter: () => 4 * dist(a, b),
  };
}

function dist(a: { X: () => number; Y: () => number }, b: { X: () => number; Y: () => number }): number {
  const dx = b.X() - a.X();
  const dy = b.Y() - a.Y();
  return Math.sqrt(dx * dx + dy * dy);
}
